import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import {
  api,
  type MetaLeadConnection,
  type MetaLeadResponsible,
  type MetaLeadRoute,
  type MetaLeadsOverview,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/Skeletons';

const KEY = ['meta-leads'];

export function MetaLeads(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<MetaLeadsOverview>('/admin/meta-leads/overview'),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Meta-заявки → Telegram</h1>
        <p className="text-sm text-muted-foreground">
          Заявки из Meta-форм уходят в тему супергруппы направления с @-упоминанием ответственного.
        </p>
      </header>

      {isLoading ? (
        <TableSkeleton columns={['Направление', 'Формы', 'Тема', 'Ответственные', '']} rows={3} />
      ) : !data || data.connections.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Связок пока нет. Заведите клиента и связку сид-командой{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">seed-sacramento-meta</code>, затем
          обновите страницу.
        </div>
      ) : (
        data.connections.map((c) => <ConnectionCard key={c.id} conn={c} />)
      )}
    </div>
  );
}

function ConnectionCard({ conn }: { conn: MetaLeadConnection }): JSX.Element {
  const qc = useQueryClient();
  const invalidate = (): void => void qc.invalidateQueries({ queryKey: KEY });
  const [chatId, setChatId] = useState(conn.telegram_chat_id ?? '');
  useEffect(() => setChatId(conn.telegram_chat_id ?? ''), [conn.telegram_chat_id]);

  const saveChat = useMutation({
    mutationFn: () =>
      api.put(`/admin/meta-leads/connections/${conn.id}`, { telegram_chat_id: chatId || null }),
    onSuccess: invalidate,
  });
  const respName = new Map(conn.responsibles.map((r) => [r.id, r.full_name]));

  return (
    <div className="space-y-5 rounded-lg border border-border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">{conn.client_name}</div>
          <div className="text-xs text-muted-foreground">{conn.label}</div>
        </div>
        <StatusTag status={conn.status} />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor={`chat-${conn.id}`}>ID супергруппы (Telegram)</Label>
          <Input
            id={`chat-${conn.id}`}
            className="w-64 font-mono text-xs"
            placeholder="-1001234567890"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={saveChat.isPending || chatId === (conn.telegram_chat_id ?? '')}
          onClick={() => saveChat.mutate()}
        >
          <Save className="h-4 w-4" /> Сохранить
        </Button>
      </div>

      <ResponsiblesSection conn={conn} onChange={invalidate} />
      <RoutesSection conn={conn} respName={respName} onChange={invalidate} />
    </div>
  );
}

function StatusTag({ status }: { status: 'active' | 'paused' }): JSX.Element {
  return (
    <span
      className={
        status === 'active'
          ? 'rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600'
          : 'rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground'
      }
    >
      {status === 'active' ? 'активна' : 'на паузе'}
    </span>
  );
}

/* ─────────── ответственные ─────────── */

function ResponsiblesSection({
  conn,
  onChange,
}: {
  conn: MetaLeadConnection;
  onChange: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [tgId, setTgId] = useState('');
  const [username, setUsername] = useState('');

  const openNew = (): void => {
    setEditId(null);
    setFullName('');
    setPhone('');
    setTgId('');
    setUsername('');
    setOpen(true);
  };
  const openEdit = (r: MetaLeadResponsible): void => {
    setEditId(r.id);
    setFullName(r.full_name);
    setPhone(r.phone ?? '');
    setTgId(r.tg_user_id ?? '');
    setUsername(r.tg_username ?? '');
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        full_name: fullName,
        phone: phone || null,
        tg_user_id: tgId ? Number(tgId) : null,
        tg_username: username || null,
      };
      return editId
        ? api.put(`/admin/meta-leads/responsibles/${editId}`, payload)
        : api.post(`/admin/meta-leads/connections/${conn.id}/responsibles`, payload);
    },
    onSuccess: () => {
      setOpen(false);
      onChange();
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/meta-leads/responsibles/${id}`),
    onSuccess: onChange,
  });

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Ответственные</h3>
        <Button variant="outline" size="sm" onClick={openNew}>
          <Plus className="h-4 w-4" /> Добавить
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
            >
              <DialogHeader>
                <DialogTitle>{editId ? 'Ответственный' : 'Новый ответственный'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="rn">Имя</Label>
                <Input id="rn" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rtid">Telegram ID</Label>
                <Input
                  id="rtid"
                  inputMode="numeric"
                  placeholder="123456789"
                  value={tgId}
                  onChange={(e) => setTgId(e.target.value.replace(/[^\d]/g, ''))}
                />
                <p className="text-xs text-muted-foreground">
                  Самый надёжный способ пинга. Узнать ID: написать боту @userinfobot команду
                  /start. Руководитель должен быть участником группы.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp">Телефон</Label>
                <Input
                  id="rp"
                  placeholder="+1 916 000-0000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Если ID неизвестен — по телефону бот подхватит ID, когда руководитель поделится
                  своим контактом с ботом (в личке → 📎 → Контакт → свой номер).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ru">Telegram @username (необязательно)</Label>
                <Input
                  id="ru"
                  placeholder="ivanov"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? 'Сохраняем…' : 'Сохранить'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Имя</TableHead>
              <TableHead>Телефон</TableHead>
              <TableHead>@username</TableHead>
              <TableHead>ID для пинга</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {conn.responsibles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-5 text-center text-muted-foreground">
                  Ответственных пока нет.
                </TableCell>
              </TableRow>
            )}
            {conn.responsibles.map((r: MetaLeadResponsible) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.full_name}</TableCell>
                <TableCell>{r.phone ?? '—'}</TableCell>
                <TableCell>{r.tg_username ? `@${r.tg_username}` : '—'}</TableCell>
                <TableCell>
                  {r.tg_user_id ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <Check className="h-3.5 w-3.5" /> подхвачен
                    </span>
                  ) : (
                    <span className="text-muted-foreground">ждём сообщения</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      if (window.confirm(`Удалить ответственного «${r.full_name}»?`)) del.mutate(r.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

/* ─────────── маршруты (направления) ─────────── */

interface RouteForm {
  direction_key: string;
  label: string;
  form_ids: string;
  campaign_ids: string;
  message_thread_id: string;
  responsible_ids: string[];
  status: 'active' | 'paused';
}

const EMPTY_ROUTE: RouteForm = {
  direction_key: '',
  label: '',
  form_ids: '',
  campaign_ids: '',
  message_thread_id: '',
  responsible_ids: [],
  status: 'active',
};

function RoutesSection({
  conn,
  respName,
  onChange,
}: {
  conn: MetaLeadConnection;
  respName: Map<string, string>;
  onChange: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<RouteForm>(EMPTY_ROUTE);

  const openNew = (): void => {
    setEditId(null);
    setForm(EMPTY_ROUTE);
    setOpen(true);
  };
  const openEdit = (r: MetaLeadRoute): void => {
    setEditId(r.id);
    setForm({
      direction_key: r.direction_key,
      label: r.label,
      form_ids: r.form_ids.join(', '),
      campaign_ids: r.campaign_ids.join(', '),
      message_thread_id: r.message_thread_id != null ? String(r.message_thread_id) : '',
      responsible_ids: r.responsible_ids,
      status: r.status,
    });
    setOpen(true);
  };

  const commaList = (s: string): string[] =>
    s.split(',').map((x) => x.trim()).filter(Boolean);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        direction_key: form.direction_key,
        label: form.label,
        form_ids: commaList(form.form_ids),
        campaign_ids: commaList(form.campaign_ids),
        message_thread_id: form.message_thread_id ? Number(form.message_thread_id) : null,
        responsible_ids: form.responsible_ids,
        status: form.status,
      };
      return editId
        ? api.put(`/admin/meta-leads/routes/${editId}`, payload)
        : api.post(`/admin/meta-leads/connections/${conn.id}/routes`, payload);
    },
    onSuccess: () => {
      setOpen(false);
      onChange();
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/meta-leads/routes/${id}`),
    onSuccess: onChange,
  });

  const toggleResp = (id: string): void =>
    setForm((f) => ({
      ...f,
      responsible_ids: f.responsible_ids.includes(id)
        ? f.responsible_ids.filter((x) => x !== id)
        : [...f.responsible_ids, id],
    }));

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Направления</h3>
        <Button variant="outline" size="sm" onClick={openNew}>
          <Plus className="h-4 w-4" /> Направление
        </Button>
      </div>

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Направление</TableHead>
              <TableHead>Формы</TableHead>
              <TableHead>Тема</TableHead>
              <TableHead>Ответственные</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {conn.routes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-5 text-center text-muted-foreground">
                  Направлений пока нет.
                </TableCell>
              </TableRow>
            )}
            {conn.routes.map((r: MetaLeadRoute) => (
              <TableRow key={r.id}>
                <TableCell>
                  <div className="font-medium">{r.label}</div>
                  <code className="text-xs text-muted-foreground">{r.direction_key}</code>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {r.form_ids.length} форм · {r.campaign_ids.length} камп.
                </TableCell>
                <TableCell>{r.message_thread_id ?? '—'}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {r.responsible_ids.length === 0 ? (
                      <span className="text-xs text-destructive">не назначен</span>
                    ) : (
                      r.responsible_ids.map((id) => (
                        <span
                          key={id}
                          className="rounded bg-muted px-1.5 py-0.5 text-xs"
                        >
                          {respName.get(id) ?? '?'}
                        </span>
                      ))
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <StatusTag status={r.status} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      if (window.confirm(`Удалить направление «${r.label}»?`)) del.mutate(r.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>{editId ? 'Направление' : 'Новое направление'}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="dk">Ключ (латиницей)</Label>
                <Input
                  id="dk"
                  placeholder="sledopyty"
                  value={form.direction_key}
                  onChange={(e) => setForm({ ...form, direction_key: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lb">Название</Label>
                <Input
                  id="lb"
                  placeholder="Следопыты"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fi">ID форм (через запятую)</Label>
              <Input
                id="fi"
                className="font-mono text-xs"
                placeholder="2485206651964401, 1721746182390543"
                value={form.form_ids}
                onChange={(e) => setForm({ ...form, form_ids: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="ci">ID кампаний</Label>
                <Input
                  id="ci"
                  className="font-mono text-xs"
                  placeholder="120256..."
                  value={form.campaign_ids}
                  onChange={(e) => setForm({ ...form, campaign_ids: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mt">ID темы (thread)</Label>
                <Input
                  id="mt"
                  type="number"
                  placeholder="2"
                  value={form.message_thread_id}
                  onChange={(e) => setForm({ ...form, message_thread_id: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ответственные</Label>
              {conn.responsibles.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Сначала добавьте ответственных выше.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {conn.responsibles.map((r) => (
                    <label
                      key={r.id}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-2 py-1 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form.responsible_ids.includes(r.id)}
                        onChange={() => toggleResp(r.id)}
                      />
                      {r.full_name}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="st">Статус</Label>
              <select
                id="st"
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as 'active' | 'paused' })
                }
              >
                <option value="active">активна</option>
                <option value="paused">на паузе</option>
              </select>
            </div>
            {save.error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                {(save.error as Error).message}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
